import React from "react";
import PropTypes from "prop-types";
import Yup from "yup";
import { Form, Formik } from "formik";
import get from "lodash/get";
import has from "lodash/has";
import find from "lodash/find";
import { withStyles } from "@mui/styles";
import {
  MenuItem,
  TextField,
  Select,
  Button,
  FormControl,
  FormHelperText,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import timezones from "common/timezone";
import { regionCodes, countryCodes, regions } from "common/location";
import SimpleSnackbar from "common/components/SimpleSnackbar";
import Panel from "common/components/Panel";
import { FORM_SUBMISSION_ERROR } from "common/constants";

const validationSchema = Yup.object().shape({
  name: Yup.string().required("City name is required"),
  region: Yup.string()
    .required("Region is required")
    .oneOf(regionCodes, "Region is invalid"),
  country: Yup.string()
    .required("Country is required")
    .oneOf(countryCodes, "Country is invalid"),
  timezoneName: Yup.string()
    .required("Timezone is required")
    .oneOf(Object.keys(timezones), "Timezone is not valid"),
});

const CityForm = ({ classes, handleSubmit, initialValues, isEdit }) => (
  <Panel title={isEdit ? "Edit City" : "Create City"}>
    <Formik
      enableReinitialize
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
      initialValues={initialValues}
    >
      {({
        values,
        errors,
        touched,
        isSubmitting,
        handleChange,
        status,
        setValues,
      }) => (
        <Form>
          <Grid container direction="column" spacing={2}>
            <Grid size={{ xs: 12 }}>
              <FormControl className={classes.formControl}>
                <TextField
                  error={touched.name && has(errors, "name")}
                  id="name"
                  type="name"
                  label="City Name"
                  margin="normal"
                  value={values.name}
                  onChange={handleChange}
                  helperText={touched.name && get(errors, "name")}
                />
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12 }}>
              <FormControl
                className={classes.formControl}
                error={touched.region && has(errors, "region")}
              >
                <Select
                  name="region"
                  id="region"
                  value={values.region}
                  onChange={(e) => {
                    const region = find(
                      regions,
                      (r) => r.code === e.target.value,
                    );

                    if (region) {
                      setValues({ ...values, country: region.countryCode });
                    }

                    return handleChange(e);
                  }}
                >
                  {regions.map((region) => (
                    <MenuItem value={region.code} key={region.code}>
                      {region.name}
                    </MenuItem>
                  ))}
                </Select>
                {touched.region && has(errors, "region") ? (
                  <FormHelperText>{errors.region}</FormHelperText>
                ) : (
                  <FormHelperText>Region</FormHelperText>
                )}
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12 }}>
              <FormControl
                className={classes.formControl}
                error={touched.timezoneName && has(errors, "timezoneName")}
              >
                <Select
                  value={values.timezoneName}
                  onChange={handleChange}
                  name="timezoneName"
                  id="timezoneName"
                  error={touched.timezoneName && has(errors, "timezoneName")}
                >
                  {Object.keys(timezones).map((key) => (
                    <MenuItem value={key} key={key}>
                      {timezones[key]}
                    </MenuItem>
                  ))}
                </Select>
                {touched.timezoneName && has(errors, "timezoneName") ? (
                  <FormHelperText>{errors.timezoneName}</FormHelperText>
                ) : (
                  <FormHelperText>Timezone</FormHelperText>
                )}
              </FormControl>
            </Grid>
          </Grid>
          <Grid size={{ xs: 12 }}>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              className={classes.button}
              disabled={isSubmitting}
            >
              {isEdit ? "Update City" : "Create City"}
            </Button>
            {has(errors, "http") && (
              <SimpleSnackbar message={FORM_SUBMISSION_ERROR} />
            )}
          </Grid>
          {get(status, "success") && (
            <SimpleSnackbar
              message={
                isEdit ? "City updated!" : "New city successfully created!"
              }
            />
          )}
        </Form>
      )}
    </Formik>
  </Panel>
);

const styles = (theme) => ({
  button: {
    marginTop: theme.spacing(2),
  },
  paper: {
    paddingTop: 16,
    paddingBottom: 16,
    marginTop: theme.spacing(3),
    width: "80%",
  },
  formControl: {
    margin: theme.spacing(1),
    minWidth: 180,
  },
});

CityForm.propTypes = {
  classes: PropTypes.object.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  initialValues: PropTypes.object,
  isEdit: PropTypes.bool,
};

export default withStyles(styles)(CityForm);
